import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Skeleton from '@/components/ui/Skeleton'

/*
 * Market · Floorsheet before its first response: the LOADED board, block for block, with a full
 * page of ten trades (the URL's default page size):
 *
 *   summary         the title row (title, 36px date button), 24, the 120-tall stat panel
 *   filters         three labelled fields: Symbol left, Buyer and Seller right
 *   table + pager   head band, 10 × 60 rows, then Pagination's own 24px and 32px row
 *   insights        the "Where the money went" line over two five-row cards
 *
 * Bars only, no words, so nothing here can be mistaken for the loaded content.
 */

const TABLE_ROWS = 10
const INSIGHT_ROWS = 5

function SummarySkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-2">
        <Skeleton className="mt-1 h-4 w-40 rounded-xs" />
        <Skeleton className="h-9 w-36 rounded-sm" />
      </div>
      <Skeleton className="h-30 w-full" />
    </div>
  )
}

/** A label over a 36 field (44 below `lg`), as FloorsheetFilters draws each one. */
function FieldSkeleton({ className }: { className: string }) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <Skeleton className="h-4 w-12 rounded-xs" />
      <Skeleton className="h-11 w-full rounded-sm lg:h-9" />
    </div>
  )
}

function FilterRowSkeleton() {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <FieldSkeleton className="w-full @sm:w-60" />
      <div className="flex w-full gap-3 @sm:w-auto">
        <FieldSkeleton className="flex-1 @sm:w-44 @sm:flex-none" />
        <FieldSkeleton className="flex-1 @sm:w-44 @sm:flex-none" />
      </div>
    </div>
  )
}

function InsightCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 bg-card px-5 pt-4 pb-5">
      <Skeleton className="h-5 w-40 rounded-xs" />
      <Skeleton className="h-4 w-full max-w-sm rounded-xs" />
      {Array.from({ length: INSIGHT_ROWS }, (_, index) => (
        <div key={index} className="flex h-5 items-center gap-3">
          <Skeleton className="h-3 flex-1 rounded-xs" />
          <Skeleton className="h-2 flex-1" />
          <Skeleton className="h-3 flex-1 rounded-xs" />
        </div>
      ))}
    </div>
  )
}

export function FloorsheetBoardSkeleton() {
  return (
    <>
      <SummarySkeleton />
      <FilterRowSkeleton />
      <div>
        <DataTableSkeleton rows={TABLE_ROWS} columns={6} rowHeight="lg" />
        <Skeleton className="mt-6 h-8 w-full" />
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-4 w-36 rounded-xs" />
        <div className="grid gap-6 @lg:grid-cols-2">
          <InsightCardSkeleton />
          <InsightCardSkeleton />
        </div>
      </div>
    </>
  )
}
